import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { concat } from 'lodash-es';
import { DraggableGrid, useOverlayContext, TextButton, Typography, useStateInRef, useDevice } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { AddServicesButton } from '~/components/AddServicesButton/AddServicesButton';
import EditServiceContent from '~/components/EditServiceContentModal/EditServiceContentModal';
import { ServicesListStructure } from '~/components/ServicesListStructure/ServicesListStructure';
import { useEventReporter } from '~/event-reporter';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { scrollIntoView } from '~/infra/utils';
import { serviceModel } from '~/models';
import { validateBillingDayOfMonth } from '~/models/service';
import { useAppConfig } from '~/store/store-context';
import { TemplateVariablesPortalProvider, useTemplateVariables } from '../../TemplateVariables/TemplateVariablesContext';
import DraftServiceInstance from './DraftServiceInstance/DraftServiceInstance';
import moveItem from './moveItem';
import styles from './ServicePackages.module.scss';
import ServicePackagesTab from './ServicePackagesTab';
const tabPanelId = 'services-in-pkg';
const autoprefix = 'formling-statement-of-work-';
const addServicesTestId = autoprefix + 'btn-add-service';
function getFreeNewBundleName(existingNames) {
    let i = 0;
    let name = '';
    do {
        i++;
        name = "Option ".concat(i);
    }while (existingNames.includes(name));
    return name;
}
function DraggableServiceCard(param) {
    let { renderService, isDragActive, dragHandle } = param;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: renderService(isDragActive, dragHandle)
    });
}
function DraggablePackageTab(param) {
    let { bundle, isSelected, isReadonly, testId, bundlesCount, bundleIndex, onClick, onRemove, onChangeName, onDuplicate, onMoveLeft, onMoveRight, isDragActive, dragHandle } = param;
    return /*#__PURE__*/ _jsx(ServicePackagesTab, {
        bundle: bundle,
        isSelected: isSelected,
        isReadonly: isReadonly,
        testId: testId,
        bundlesCount: bundlesCount,
        bundleIndex: bundleIndex,
        onClick: onClick,
        onRemove: onRemove,
        onChangeName: onChangeName,
        onDuplicate: onDuplicate,
        onMoveLeft: onMoveLeft,
        onMoveRight: onMoveRight,
        isDragActive: isDragActive,
        dragHandle: dragHandle
    });
}
const ServicePackages = /*#__PURE__*/ forwardRef(function ServicePackages(props, ref) {
    var _bundles_;
    const { t: texts } = useLocaleKeys('components.agreementStudio.servicePackages');
    const { services, bundles, agreementData, showEmptyWarning, showInvalidPackagesWarning, showServiceValidationWarning, showErrors, scrollToServiceId, isReadOnly, advancedSettingsProps, isServiceInvalid, setServices, setBundles, onHasError } = props;
    const { isMobileDevice } = useDevice();
    const { reportButtonClick } = useEventReporter();
    const { DRUGS_AND_DROPS: isDragAndDropEnabled = false } = useFeatureFlags();
    const [selectedPackage, setSelectedPackage] = useState((_bundles_ = bundles[0]) === null || _bundles_ === void 0 ? void 0 : _bundles_.id);
    const [isDraggingService, setIsDraggingService] = useState(false);
    const getStateInRef = useStateInRef(props);
    const servicesRef = useRef({});
    const scrollToServiceCounter = useRef(1);
    const overlayContext = useOverlayContext();
    const { disableScrollAnimation } = useAppConfig();
    const templateVariablesValue = useTemplateVariables();
    const isInPackage = bundles.length > 1;
    useEffect(()=>{
        const hasError = services.reduce((anyError, service)=>{
            const billingDayOfMonth = serviceModel.billingDayOfMonth(service);
            const isBilledUpfront = serviceModel.isBilledUpfront(service);
            const serviceHasError = billingDayOfMonth != null && isBilledUpfront != null ? validateBillingDayOfMonth(billingDayOfMonth, isBilledUpfront) : false;
            return anyError || serviceHasError;
        }, false);
        onHasError && onHasError(hasError);
    }, [
        services,
        onHasError
    ]);
    useEffect(()=>{
        if (scrollToServiceId) {
            const { bundles, services } = getStateInRef();
            if (bundles.length > 0) {
                const newSelectedPackage = bundles.find((b)=>b.services.some((s)=>s.id === scrollToServiceId));
                if (newSelectedPackage) {
                    setSelectedPackage(newSelectedPackage.id);
                    const serviceToScrollTo = newSelectedPackage.services.find((s)=>s.id === scrollToServiceId);
                    serviceToScrollTo && scrollToService(serviceToScrollTo);
                }
            } else {
                const serviceToScrollTo = services.find((s)=>s.id === scrollToServiceId);
                serviceToScrollTo && scrollToService(serviceToScrollTo);
            }
        }
    }, [
        scrollToServiceId
    ]);
    useEffect(()=>{
        // We will get here if we try to send an agreement that contains a bundle without services.
        if (showEmptyWarning) {
            const { bundles } = getStateInRef();
            const emptyBundle = bundles.find((b)=>!b.services.length);
            emptyBundle && setSelectedPackage(emptyBundle.id);
        }
    }, [
        showEmptyWarning
    ]);
    const actualServices = useMemo(()=>{
        if (bundles.length) {
            const activeBundle = bundles.find((b)=>b.id === selectedPackage);
            return activeBundle ? activeBundle.services : services;
        }
        return services;
    }, [
        selectedPackage,
        bundles,
        services
    ]);
    const onAddPackage = ()=>{
        const firstBundleId = generateUniqueId('bundle');
        if (!bundles.length) {
            const firstName = getFreeNewBundleName([]);
            const secondName = getFreeNewBundleName([
                firstName
            ]);
            setSelectedPackage(firstBundleId);
            setBundles([
                {
                    id: firstBundleId,
                    name: firstName,
                    services
                },
                {
                    id: generateUniqueId('bundle'),
                    name: secondName,
                    services: []
                }
            ]);
            setServices([]);
        } else {
            setBundles([
                ...bundles,
                {
                    id: generateUniqueId('bundle'),
                    name: getFreeNewBundleName(bundles.map((b)=>b.name)),
                    services: []
                }
            ]);
        }
    };
    const onRemovePackage = (id)=>{
        const newBundles = bundles.filter((b)=>b.id !== id);
        if (newBundles.length > 1) {
            setSelectedPackage(newBundles[0].id);
            setBundles(newBundles);
        } else {
            setSelectedPackage(undefined);
            setBundles([]);
            setServices(newBundles[0].services);
        }
    };
    const onDuplicatePackage = (param)=>{
        let { services, name } = param;
        const id = generateUniqueId('bundle');
        setBundles([
            ...bundles,
            {
                id,
                name: "".concat(name, " Copy"),
                services: services.map((_0)=>{
                    let { id: _ } = _0, service = _object_without_properties(_0, [
                        "id"
                    ]);
                    return serviceModel.makeService(service);
                })
            }
        ]);
        setSelectedPackage(id);
    };
    const onChangePackageName = (name, bundleId)=>{
        const newBundles = bundles.map((b)=>{
            if (b.id === bundleId) return _object_spread_props(_object_spread({}, b), {
                name
            });
            return b;
        });
        setBundles(newBundles);
    };
    const handleTabReorder = useCallback((reorderedItems)=>{
        setBundles(reorderedItems.map((item)=>item.bundle));
    }, [
        setBundles
    ]);
    const handleMoveTab = useCallback((bundle, direction)=>{
        setBundles(moveItem(bundle, bundles, direction));
    }, [
        bundles,
        setBundles
    ]);
    const isDraggableTabs = isDragAndDropEnabled && bundles.length >= 2 && !isReadOnly;
    const draggableTabItems = isDraggableTabs ? bundles.map((b, idx)=>({
            id: b.id,
            bundle: b,
            isSelected: selectedPackage === b.id,
            isReadonly: !!isReadOnly,
            testId: "pkg-tab-".concat(idx),
            bundlesCount: bundles.length,
            bundleIndex: idx,
            onClick: setSelectedPackage,
            onRemove: onRemovePackage,
            onChangeName: onChangePackageName,
            onDuplicate: onDuplicatePackage,
            onMoveLeft: ()=>handleMoveTab(b, 'up'),
            onMoveRight: ()=>handleMoveTab(b, 'down')
        })) : [];
    const isDraggableServices = isDragAndDropEnabled && actualServices.length >= 2 && !isReadOnly;
    const handleServiceCardReorder = useCallback((reorderedItems)=>{
        const reorderedServices = reorderedItems.map((item)=>actualServices.find((s)=>s.id === item.id)).filter((s)=>!!s);
        if (bundles.length && selectedPackage) {
            const newBundles = bundles.map((b)=>b.id === selectedPackage ? _object_spread_props(_object_spread({}, b), {
                    services: reorderedServices
                }) : b);
            setBundles(newBundles);
        } else {
            setServices(reorderedServices);
        }
    }, [
        actualServices,
        bundles,
        selectedPackage,
        setBundles,
        setServices
    ]);
    function handleServicesAdded(newServices) {
        if (bundles.length) {
            const newBundles = bundles.map((b)=>{
                if (b.id === selectedPackage) return _object_spread_props(_object_spread({}, b), {
                    services: [
                        ...b.services,
                        ...newServices
                    ]
                });
                return b;
            });
            setBundles(newBundles);
        } else {
            setServices([
                ...services,
                ...newServices
            ]);
        }
        scrollToService(newServices[newServices.length - 1]);
    }
    async function onEditServiceNameAndDescription(service) {
        const isBundle = serviceModel.isBundle(service);
        await overlayContext.showModal({
            biName: 'edit_service_content',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            openBottomSheetOnMobile: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(TemplateVariablesPortalProvider, {
                    value: templateVariablesValue,
                    children: /*#__PURE__*/ _jsx(EditServiceContent, {
                        width: "800px",
                        title: isBundle ? texts.bundleNameDescription() : texts.serviceNameDescription(),
                        serviceName: service.name,
                        description: service.description,
                        terms: service.terms,
                        serviceId: service.id,
                        dismissModal: dismissModal,
                        onSave: (param)=>{
                            let { name, description, terms } = param;
                            applyServiceChanges(_object_spread_props(_object_spread({}, service), {
                                name,
                                description,
                                terms
                            }));
                            dismissModal();
                        },
                        bundle: service.bundle,
                        showAiDescriptionPanel: true
                    })
                });
            }
        });
    }
    const applyServiceChanges = (changedService)=>{
        const applyChangedService = (services)=>{
            const idx = services.findIndex((s)=>s.id === changedService.id);
            const newServices = [
                ...services
            ];
            newServices[idx] = changedService;
            return newServices;
        };
        if (bundles.length) {
            const newBundles = bundles.map((b)=>{
                if (b.services.some((s)=>s.id === changedService.id)) return _object_spread_props(_object_spread({}, b), {
                    services: applyChangedService(b.services)
                });
                return b;
            });
            setBundles(newBundles);
        } else {
            setServices(applyChangedService(services));
        }
    };
    const onRemoveService = (removedServiceId)=>{
        if (bundles.length) {
            const newBundles = bundles.map((b)=>{
                if (b.services.some((s)=>s.id === removedServiceId)) return _object_spread_props(_object_spread({}, b), {
                    services: b.services.filter((s)=>s.id !== removedServiceId)
                });
                return b;
            });
            setBundles(newBundles);
        } else {
            setServices(services.filter((s)=>s.id !== removedServiceId));
        }
    };
    const onSortServices = (changedService, intent)=>{
        const newServices = moveItem(changedService, actualServices, intent);
        if (bundles.length) {
            const newBundles = bundles.map((b)=>{
                if (b.services.some((s)=>s.id === changedService.id)) return _object_spread_props(_object_spread({}, b), {
                    services: newServices
                });
                return b;
            });
            setBundles(newBundles);
        } else {
            setServices(newServices);
        }
    };
    function findService(serviceId) {
        if (bundles.length) {
            return concat(...bundles.map((param)=>{
                let { services } = param;
                return services;
            })).find((s)=>s.id === serviceId);
        } else {
            return services.find((s)=>s.id === serviceId);
        }
    }
    function onDayOfMonthChange(serviceId, dayOfMonth) {
        const service = findService(serviceId);
        if (service != null) applyServiceChanges(serviceModel.setBillingDayOfMonth(service, dayOfMonth));
    }
    function onDayOfWeekChange(serviceId, dayOfWeek) {
        const service = findService(serviceId);
        if (service != null) applyServiceChanges(serviceModel.setBillingDayOfWeek(service, dayOfWeek));
    }
    function onDayOfYearChange(serviceId, dayOfYear) {
        const service = findService(serviceId);
        if (service != null) applyServiceChanges(serviceModel.setBillingDayOfYear(service, dayOfYear));
    }
    function onBillUpfrontModeChange(serviceId, billUpfront) {
        const service = findService(serviceId);
        if (service != null) applyServiceChanges(serviceModel.setBillUpfront(service, billUpfront));
    }
    function renderServiceCard(service, idx, dragProps) {
        return /*#__PURE__*/ _jsx(DraftServiceInstance, {
            showErrors: showErrors,
            idx: idx,
            ref: (el)=>servicesRef.current[service.id] = el,
            service: service,
            isInvalid: isServiceInvalid(service),
            isReadOnly: isReadOnly,
            canMoveUp: 0 < idx,
            canMoveDown: idx < actualServices.length - 1,
            advancedSettingsProps: _object_spread_props(_object_spread({}, advancedSettingsProps), {
                showServiceValidationWarning: showServiceValidationWarning.some((id)=>id === serviceModel.id(service))
            }),
            serviceDetailsProps: {
                agreementData,
                relationshipState: advancedSettingsProps.relationshipState,
                onDayOfMonthChange,
                onDayOfWeekChange,
                onDayOfYearChange,
                onBillUpfrontModeChange,
                onCostChange: (cost)=>applyServiceChanges(serviceModel.setCost(service, cost)),
                onChangeService: (s)=>applyServiceChanges(s),
                onIncludedServicesChange: (includedServices)=>applyServiceChanges(serviceModel.setIncludedServices(service, includedServices)),
                onPausedChange: advancedSettingsProps.onPausedChange,
                onBillOnAcceptanceChange: advancedSettingsProps.onBillOnAcceptanceChange,
                onStartBillingAtChange: advancedSettingsProps.onStartBillingAtChange
            },
            onEditServiceNameAndDescription: onEditServiceNameAndDescription,
            onRemoveService: onRemoveService,
            onMoveDown: ()=>onSortServices(service, 'down'),
            onMoveUp: ()=>onSortServices(service, 'up'),
            isDragActive: dragProps === null || dragProps === void 0 ? void 0 : dragProps.isDragActive,
            dragHandle: dragProps === null || dragProps === void 0 ? void 0 : dragProps.dragHandle
        });
    }
    function scrollToService(serviceToScrollTo) {
        const serviceEl = servicesRef.current[serviceToScrollTo.id];
        if (!serviceEl) {
            if (scrollToServiceCounter.current < 20) {
                scrollToServiceCounter.current++;
                setTimeout(()=>scrollToService(serviceToScrollTo), 50);
            }
        } else {
            scrollToServiceCounter.current = 1;
            scrollIntoView(serviceEl, {
                disableScrollAnimation
            });
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cn({
            [styles.mobile]: isMobileDevice
        }),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.tabs,
                children: [
                    !services.length && isDraggableTabs && /*#__PURE__*/ _jsx(DraggableGrid, {
                        bi: {
                            name: 'reorder_service_package_tab'
                        },
                        items: draggableTabItems,
                        itemComponent: DraggablePackageTab,
                        onReorder: handleTabReorder,
                        dragMode: "solid",
                        layout: {
                            rows: 1
                        },
                        itemHandleVisibility: "hover",
                        itemDragActivation: "handle",
                        itemHandlePosition: "inside",
                        gap: isMobileDevice ? 8 : 24,
                        testId: "pkg-tabs-grid"
                    }),
                    !services.length && !isDraggableTabs && bundles.map((b, idx)=>/*#__PURE__*/ _jsx(ServicePackagesTab, {
                            bundle: b,
                            isSelected: selectedPackage === b.id,
                            isReadonly: isReadOnly,
                            testId: "pkg-tab-".concat(idx),
                            bundlesCount: bundles.length,
                            bundleIndex: idx,
                            onClick: setSelectedPackage,
                            onRemove: onRemovePackage,
                            onChangeName: onChangePackageName,
                            onDuplicate: onDuplicatePackage
                        }, b.id)),
                    bundles.length < 3 && !isReadOnly && /*#__PURE__*/ _jsxs(TextButton, {
                        className: styles.addPackageButton,
                        underline: "hover",
                        testId: "pkg-add-btn",
                        onClick: onAddPackage,
                        children: [
                            "+ ",
                            !bundles.length ? texts.createPackage() : texts.addPackage()
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                id: tabPanelId,
                children: [
                    /*#__PURE__*/ _jsx(ServicesListStructure, {
                        ref: ref,
                        addServicesTestId: addServicesTestId,
                        centerVertically: isInPackage,
                        isReadOnly: isReadOnly || isDraggingService,
                        agreementData: agreementData,
                        onClickAddServices: ()=>reportButtonClick(addServicesTestId),
                        onServicesAdded: handleServicesAdded,
                        emptyState: /*#__PURE__*/ _jsxs("div", {
                            className: styles.emptyStateContainer,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            skin: "secondary",
                                            as: "p",
                                            children: texts.addServicesIncluded()
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            skin: "secondary",
                                            as: "p",
                                            children: texts.servicesCanBeCustomized()
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(AddServicesButton, {
                                    text: texts.selectServices(),
                                    agreementData: agreementData,
                                    disabled: isReadOnly,
                                    testId: addServicesTestId,
                                    onClick: ()=>reportButtonClick(autoprefix + '-empty-state-btn-add-service'),
                                    onServicesAdded: handleServicesAdded
                                }),
                                showEmptyWarning && /*#__PURE__*/ _jsxs(Typography, {
                                    skin: "error",
                                    testId: autoprefix + 'empty-msg',
                                    children: [
                                        "You must add at least one service to",
                                        ' ',
                                        isInPackage ? 'this package' : 'your agreement'
                                    ]
                                })
                            ]
                        }),
                        children: isDraggableServices ? /*#__PURE__*/ _jsx("div", {
                            style: {
                                '--draggable-grid-item-radius': '12px'
                            },
                            children: /*#__PURE__*/ _jsx(DraggableGrid, {
                                bi: {
                                    name: 'reorder_service_package_service'
                                },
                                items: actualServices.map((service, idx)=>({
                                        id: service.id,
                                        renderService: (isDragActive, dragHandle)=>renderServiceCard(service, idx, {
                                                isDragActive,
                                                dragHandle
                                            })
                                    })),
                                itemComponent: DraggableServiceCard,
                                onReorder: handleServiceCardReorder,
                                onDragStateChange: setIsDraggingService,
                                dragMode: "withGhost",
                                layout: {
                                    columns: 1
                                },
                                itemDragActivation: "handle",
                                itemHandlePosition: "inside",
                                gap: 16,
                                testId: "service-cards-grid"
                            })
                        }) : actualServices.map((service, idx)=>/*#__PURE__*/ _jsx(Fragment, {
                                children: renderServiceCard(service, idx)
                            }, service.id))
                    }),
                    showInvalidPackagesWarning && /*#__PURE__*/ _jsx(Typography, {
                        className: styles.invalidWarning,
                        skin: "error",
                        testId: "invalid-pkg-warning",
                        children: "You must create at least two packages"
                    })
                ]
            })
        ]
    });
});
export default ServicePackages;
